import React, { useState } from 'react';
import { useAPI } from 'utils/api';
import { useTranslation } from 'react-i18next';
import { useGlobal } from 'context/global/GlobalContext';
import { CookieSettings } from 'types/ProductDetailsType';
import { CookiesConsentType } from 'types/CookiesConsentType';
import { useCookieConsent } from 'context/CookieConsentContext/CookieConsentContext';
import LoadingIndicator from 'components/atomic/LoadingIndicator';
import cookieCheckbox from 'assets/icons/svg/cookie-checkbox.svg';
import HtmlWrapper from 'components/wrappers/HtmlWrapper';
import Cookies from 'js-cookie';
import { ExperienceData, LinkTrackingParams } from 'utils/linkTracking';

interface UserUpdatePayload {
  brandId?: string;
  cookieConsent?: {
    consentType: string;
    allowed: boolean;
    consentFor: string;
    language: string;
    defaultLanguage: string;
    meta?: any;
  };
}

type CookieBannerProps = {
  cookieSettings: CookieSettings | null;
  linkTrackingParams?: LinkTrackingParams | null;
  experienceData?: ExperienceData | null;
};

const CookieBanner: React.FC<CookieBannerProps> = ({
  cookieSettings,
  linkTrackingParams,
  experienceData,
}) => {
  const [expanded, setExpanded] = useState(false);
  const {
    cookiesConsent,
    setCookiesConsent,
    cookieBannerChecked,
    toggleCookieBannerChecked,
  } = useCookieConsent();
  const { user, productDetails } = useGlobal();

  const { t } = useTranslation('translation', {
    keyPrefix: 'cookieBanner',
  });

  const handleCheckboxChange = (type: keyof CookiesConsentType) => {
    setCookiesConsent((prev: any) => ({ ...prev, [type]: !prev[type] }));
  };

  const getAriaLabel = (key: keyof CookiesConsentType) => {
    switch (key) {
      case 'essential':
        return t('essentialCookiesDescription');
      case 'functional':
        return t('functionalCookiesDescription');
      case 'analytics':
        return t('analyticsCookiesDescription');
      case 'marketing':
        return t('marketingCookiesDescription');
      case 'social':
        return t('socialCookiesDescription');
      default:
        return '';
    }
  };

  const [updateUser, loading] = useAPI<UserUpdatePayload>({
    method: 'PUT',
    endpoint: 'auth/update',
    onSuccess: () => {},
  });

  if (
    cookieBannerChecked ||
    Cookies.get('cookieConsent') ||
    !(
      cookieSettings?.enabled &&
      (cookieSettings?.countries?.some(
        ({ value }) => value === Cookies.get('countryCode')
      ) ||
        cookieSettings?.countries?.some(
          ({ value }) => value === Cookies.get('regionCode')
        ))
    )
  )
    return null;

  const handleAcceptAll = () => {
    const newCookiesConsent: CookiesConsentType = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      social: true,
    };
    setCookiesConsent(newCookiesConsent);
    toggleCookieBannerChecked(true);
    if (user) {
      updateUser({
        brandId: productDetails?.brand?.id,
        cookieConsent: {
          allowed: newCookiesConsent?.essential || true,
          consentType: cookieBannerChecked ? 'explicit' : 'implicit',
          consentFor: 'cookies',
          language: cookieSettings?.description || '',
          defaultLanguage: '',
          meta: newCookiesConsent
            ? newCookiesConsent
            : {
                essential: true,
                functional: false,
                analytics: false,
                marketing: false,
                social: false,
              },
        },
      });
    }
  };

  const handleAcceptSelected = () => {
    const newCookiesConsent = {
      ...cookiesConsent,
    };
    setCookiesConsent(newCookiesConsent);
    toggleCookieBannerChecked(true);
    if (user) {
      updateUser({
        brandId: productDetails?.brand?.id,
        cookieConsent: {
          allowed: newCookiesConsent?.essential || true,
          consentType: cookieBannerChecked ? 'explicit' : 'implicit',
          consentFor: 'cookies',
          language: cookieSettings?.description || '',
          defaultLanguage: '',
          meta: newCookiesConsent
            ? newCookiesConsent
            : {
                essential: true,
                functional: false,
                analytics: false,
                marketing: false,
                social: false,
              },
        },
      });
    }
  };

  return (
    <section
      className={`z-[9999] absolute top-0 left-0 w-full h-full bg-black/50 text-white text-center`}
    >
      <div className='relative w-full flex flex-col items-start justify-start px-4 pt-2 pb-4 gap-3 bg-white rounded-t-none rounded-b-3xl'>
        <div className='relative w-full flex flex-col items-start justify-start text-left px-1 gap-0.5'>
          <h6
            tabIndex={1}
            aria-label='Accept the use of Cookies'
            className='font-semibold text-black'
          >
            {t('bannerHeading')}
          </h6>
          <HtmlWrapper
            styles='!mt-0.5 !text-black !text-xs'
            html={cookieSettings?.description || ''}
            linkTrackingParams={linkTrackingParams}
            experienceData={experienceData}
          />
        </div>
        {expanded && (
          <div className='relative w-full flex flex-col items-start justify-start p-3 gap-2.5 rounded-xl border border-solid border-lightBorder'>
            {Object.keys(cookiesConsent).map((key: string) => (
              <button
                key={key}
                tabIndex={1}
                type='button'
                disabled={key === 'essential'}
                onClick={() =>
                  handleCheckboxChange(key as keyof CookiesConsentType)
                }
                className={`w-full flex items-center gap-2 text-black ${
                  key === 'essential' && 'pointer-events-none'
                }`}
                aria-label={
                  cookiesConsent[key as keyof CookiesConsentType]
                    ? `${t('checkedStatus')}: ${getAriaLabel(
                        key as keyof CookiesConsentType
                      )}`
                    : `${t('uncheckedStatus')}: ${getAriaLabel(
                        key as keyof CookiesConsentType
                      )}`
                }
              >
                <label
                  className={`
                    flex-shrink-0 w-5 h-5 min-w-5 min-h-5 flex items-center justify-center rounded border-solid focus:ring-black cursor-pointer 
                    ${
                      cookiesConsent[key as keyof CookiesConsentType] === true
                        ? 'border-transparent'
                        : 'border-gray-300'
                    }
                  `}
                >
                  {cookiesConsent[key as keyof CookiesConsentType] === true ? (
                    <img
                      aria-hidden
                      alt='checkbox'
                      src={cookieCheckbox}
                      className={`w-5 h-5 min-w-5 min-h-5 pointer-events-none ${
                        key === 'essential' && 'opacity-60'
                      }`}
                    />
                  ) : (
                    <div
                      aria-hidden
                      className='!w-[17px] !h-[17px] !min-w-[17px] !min-h-[17px] pointer-events-none'
                    />
                  )}
                </label>
                <span
                  className={`text-left text-xs text-black cursor-pointer ${
                    key === 'essential' && 'opacity-60'
                  }`}
                >
                  {key === 'essential' && t('essentialCookiesDescription')}
                  {key === 'functional' && t('functionalCookiesDescription')}
                  {key === 'analytics' && t('analyticsCookiesDescription')}
                  {key === 'marketing' && t('marketingCookiesDescription')}
                  {key === 'social' && t('socialCookiesDescription')}
                </span>
              </button>
            ))}
          </div>
        )}
        <div className='relative w-full flex flex-row items-center justify-between gap-2'>
          {loading ? (
            <LoadingIndicator size='40px' />
          ) : (
            <>
              <button
                type='button'
                tabIndex={1}
                onClick={() => setExpanded(!expanded)}
                className={`${
                  expanded ? 'w-full max-w-max min-w-max' : 'w-full'
                } h-10 px-4 text-sm sm:text-base leading-[100%] shadow text-primary font-semibold rounded-full bg-white border border-solid border-primary`}
              >
                {expanded ? t('buttons.cancel') : t('buttons.configure')}
              </button>
              <button
                type='button'
                tabIndex={1}
                onClick={expanded ? handleAcceptSelected : handleAcceptAll}
                className='w-full h-10 px-4 text-sm sm:text-base leading-[100%] shadow text-white font-semibold bg-primary text-white rounded-full'
              >
                {expanded
                  ? t('buttons.acceptSelected')
                  : t('buttons.acceptAll')}
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default CookieBanner;
